import type { BreakdownDim, MonthCash, PaymentMethod, RangeKey } from '../types'
import { levelValues, type Series } from './levels'

export const rangeLen = (range: RangeKey) => (range === '1M' ? 1 : range === '3M' ? 3 : range === '6M' ? 6 : 12)

/** The months in a window: a range length ending at `endKey` (YTD = that year to date). */
export function windowMonths(months: MonthCash[], endKey: string, range: RangeKey): MonthCash[] {
  const endIdx = months.findIndex((m) => m.key === endKey)
  if (endIdx < 0) return months.slice(-1)
  if (range === 'YTD') {
    const y = months[endIdx].year
    const startIdx = months.findIndex((m) => m.year === y)
    return months.slice(startIdx < 0 ? endIdx : startIdx, endIdx + 1)
  }
  const n = rangeLen(range)
  return months.slice(Math.max(0, endIdx - n + 1), endIdx + 1)
}

/** The equal-length window immediately before `win`. */
export function priorWindow(months: MonthCash[], win: MonthCash[]): MonthCash[] {
  if (!win.length) return []
  const firstIdx = months.findIndex((m) => m.key === win[0].key)
  return months.slice(Math.max(0, firstIdx - win.length), firstIdx)
}

export interface Agg {
  netCash: number
  netSales: number
  gross: number
  refunds: number
  fees: number
  fx: number
  cost: number
  count: number
  payments: Record<string, number>
  /** Schema v2 level maps, summed over the window (see ./levels). */
  unit: Record<string, number>
  department: Record<string, number>
  category: Record<string, number>
  departmentUnresolved: Record<string, number>
}

const addInto = (t: Record<string, number>, s: Record<string, number> | undefined) => {
  if (!s) return
  for (const k in s) t[k] = (t[k] || 0) + s[k]
}

export function agg(ms: MonthCash[]): Agg {
  const a: Agg = { netCash: 0, netSales: 0, gross: 0, refunds: 0, fees: 0, fx: 0, cost: 0, count: ms.length, payments: {}, unit: {}, department: {}, category: {}, departmentUnresolved: {} }
  for (const m of ms) {
    a.netCash += m.netCash
    a.netSales += m.netSales
    a.gross += m.gross
    a.refunds += m.refunds
    a.fees += m.fees
    a.fx += m.fx
    a.cost += m.cost
    addInto(a.payments, m.payments)
    addInto(a.unit, m.unit)
    addInto(a.department, m.department)
    addInto(a.category, m.category)
    addInto(a.departmentUnresolved, m.departmentUnresolved)
  }
  return a
}

export interface MoverRow {
  /** Hierarchy id (keys `moverDetails`). */
  key: string
  name: string
  curr: number
  prev: number
  delta: number
  dir: 'gain' | 'decline'
  pct: number
}

/** Movers = current-window totals at the chosen level vs the prior window.
 *  Series with nothing in either window are left out. */
export function computeMovers(cur: Agg, prev: Agg, dim: BreakdownDim, series: Series[]): MoverRow[] {
  const c = levelValues(cur, dim)
  const p = levelValues(prev, dim)
  return series
    .map((s) => {
      const curr = c[s.key] || 0
      const prior = p[s.key] || 0
      const delta = curr - prior
      return {
        key: s.key,
        name: s.label,
        curr,
        prev: prior,
        delta,
        dir: (delta >= 0 ? 'gain' : 'decline') as 'gain' | 'decline',
        pct: prior ? Math.round((delta / prior) * 100) : 0,
      }
    })
    .filter((m) => m.curr || m.prev)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
}

/** Scale all of a month's figures by an entity share (illustrative entity filter). */
export function scaleMonths(months: MonthCash[], share: number): MonthCash[] {
  if (share === 1) return months
  const sc = (n: number) => Math.round(n * share)
  const scObj = (o: Record<string, number> | undefined) => Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [k, sc(v)]))
  return months.map((m) => ({
    ...m,
    gross: sc(m.gross),
    refunds: sc(m.refunds),
    netSales: sc(m.netSales),
    fees: sc(m.fees),
    fx: sc(m.fx),
    netCash: sc(m.netCash),
    cost: sc(m.cost),
    payments: scObj(m.payments),
    unit: scObj(m.unit),
    department: scObj(m.department),
    category: scObj(m.category),
    departmentUnresolved: scObj(m.departmentUnresolved),
  }))
}

export function spanLabel(ms: MonthCash[]): string {
  if (!ms.length) return '—'
  if (ms.length === 1) return ms[0].label
  return `${ms[0].label} – ${ms[ms.length - 1].label}`
}

export const paymentList = (a: Agg, methods: PaymentMethod[]) => methods.map((pm) => ({ ...pm, eur: a.payments[pm.key] || 0 }))

export const deltaPct = (curr: number, prev: number) => (prev ? Math.round(((curr - prev) / prev) * 1000) / 10 : 0)
